/**
 * `MarketResolved` → market-row patcher.
 *
 * The dedicated `morfi.market.{m}.resolution.v0` subject (proto
 * `morfi.v1.MarketResolved`) fires exactly once when a market resolves. Besides
 * the terminal `status`, the payload carries the chain timestamp (`ts`) and the
 * winning mint (`winningMint`/`winning_mint`), and final conviction. We stamp
 * those onto rows when the fields exist so open detail pages can freeze the
 * consensus chart immediately on the realtime event instead of waiting for a
 * full refetch.
 *
 * `status` value set sourced from `openapi.json`:
 *   ["created","baking","trading","resolved","graduated"].
 *
 * Immutable: returns a NEW object only when at least one field actually
 * changes; otherwise the exact `prev` reference (idempotent on replay).
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

interface ResolutionOutcomeLike {
  readonly convictionAtResolution?: string;
  readonly mint?: string;
  readonly outcomeIndex?: number | string;
}

interface ResolutionRowLike {
  readonly status?: string;
  readonly resolvedAt?: string | null;
  readonly winnerOutcomeIndex?: number | null;
  readonly outcomes?: ReadonlyArray<ResolutionOutcomeLike>;
}

function coerceUnixSeconds(value: unknown): number | null {
  if (typeof value === 'bigint') {
    return value > 0n && value <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(value) : null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? Math.trunc(value) : null;
  }
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
  }
  return null;
}

function coerceWinnerMint(raw: Record<string, unknown>): string | null {
  const camel = raw.winningMint;
  if (typeof camel === 'string' && camel.length > 0) return camel;
  const snake = raw.winning_mint;
  return typeof snake === 'string' && snake.length > 0 ? snake : null;
}

function coerceOutcomeIndex(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number(value);
  return null;
}

function coercePpb(value: unknown): string | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
    ? String(value)
    : null;
}

function deriveWinnerOutcomeIndex(
  outcomes: ReadonlyArray<ResolutionOutcomeLike> | undefined,
  winningMint: string | null,
): number | null {
  if (winningMint === null || outcomes === undefined) return null;
  for (const outcome of outcomes) {
    if (outcome.mint !== winningMint) continue;
    const outcomeIndex = coerceOutcomeIndex(outcome.outcomeIndex);
    if (outcomeIndex !== null) return outcomeIndex;
  }
  return null;
}

export function mapResolvedStatus<T extends ResolutionRowLike>(
  prev: T,
  raw: Record<string, unknown>,
): T {
  let next = prev;

  // Graduation can arrive first on its separate lifecycle subject.
  if (prev.status !== 'resolved' && prev.status !== 'graduated') {
    next = { ...next, status: 'resolved' };
  }

  const ts = coerceUnixSeconds(raw.ts);
  if (ts !== null) {
    const resolvedAt = new Date(ts * 1000).toISOString();
    if (next.resolvedAt !== resolvedAt) {
      next = { ...next, resolvedAt };
    }
  }

  const winningMint = coerceWinnerMint(raw);
  const winnerOutcomeIndex = deriveWinnerOutcomeIndex(prev.outcomes, winningMint);
  if (winnerOutcomeIndex !== null && next.winnerOutcomeIndex !== winnerOutcomeIndex) {
    next = { ...next, winnerOutcomeIndex };
  }

  const winningConvictionAtResolution = coercePpb(raw.winningConvictionAtResolutionPpb);
  if (
    winningMint !== null &&
    winningConvictionAtResolution !== null &&
    next.outcomes !== undefined
  ) {
    const outcomeIndex = next.outcomes.findIndex((outcome) => outcome.mint === winningMint);
    const outcome = next.outcomes[outcomeIndex];
    if (outcome !== undefined && outcome.convictionAtResolution !== winningConvictionAtResolution) {
      const outcomes = [...next.outcomes];
      outcomes[outcomeIndex] = {
        ...outcome,
        convictionAtResolution: winningConvictionAtResolution,
      };
      next = { ...next, outcomes } as T;
    }
  }

  return next === prev ? prev : (next as T);
}
